// Base Libs
import { useContext, useState } from "react";
import { GuxFormFieldTextarea, GuxIcon } from "genesys-spark-components-react";
import { shallowEqual } from "react-redux";

import {
    AssessmentFormQuestionTypeEnum,
    EvaluationQuestionTypeEnum,
    SurveyQuestionTypeEnum,
} from "@genesys/cloud-frontend/models";

// Styles
import "./QuestionOperations.less";

// Feature commons
import { useStrings } from "features/i18n/useStrings";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { DefaultLimits } from "constants/DefaultLimits";
import FormType from "enums/FormType";

// Feature slices
import {
    getMultipleSelectQuestionOptionsMapping,
    selectCurrentAnswerIdsForMSQ,
    selectFormType,
    selectQuestion,
} from "features/qualityForm/qualityFormSlices/qualityFormCommons";
import {
    getPreviewScores,
    scorableSliceActions,
    selectCommentCharLimitDateCheck,
    selectQuestionScore,
    sendAnswersDataOnFormDataChange,
} from "features/scorable/scorableSlice";

// Locales
import strings from "./CommonQuestionComponents.strings";

// Locals
import { PreviewModeContext } from "../Contexts";
import { RemainingCharacters } from "../RemainingCharacters";

export default function QuestionOperations({
    questionId,
}: {
    questionId: string;
}) {
    const t = useStrings(strings);
    const dispatch = useAppDispatch();
    const {
        answerId,
        markedNa,
        npsScore,
        comments,
        freeTextAnswer,
        npsTextAnswer,
        naEnabled,
        isMultipleSelect,
        currentAnswerIdsForMSQ,
        multipleSelectQuestionOptionIdMappings,
    } = useAppSelector((state) => {
        const questionScore = selectQuestionScore(state, questionId);
        const question = selectQuestion(state, questionId);
        const currentAnswerIdsForMSQ = selectCurrentAnswerIdsForMSQ(
            state,
            questionId
        ) as string[];

        let multipleSelectQuestionOptionIdMappings;
        if (
            question?.type === EvaluationQuestionTypeEnum.MultipleSelectQuestion
        ) {
            multipleSelectQuestionOptionIdMappings =
                getMultipleSelectQuestionOptionsMapping(state, questionId);
        } else {
            multipleSelectQuestionOptionIdMappings = undefined;
        }

        return {
            answerId: questionScore?.answerId,
            markedNa: questionScore?.markedNA,
            npsScore: questionScore?.npsScore,
            comments: questionScore?.comments,
            freeTextAnswer: questionScore?.freeTextAnswer,
            npsTextAnswer: questionScore?.npsTextAnswer,
            naEnabled: question?.naEnabled ?? false,
            isMultipleSelect:
                question?.type ===
                EvaluationQuestionTypeEnum.MultipleSelectQuestion,
            currentAnswerIdsForMSQ: currentAnswerIdsForMSQ,
            multipleSelectQuestionOptionIdMappings:
                multipleSelectQuestionOptionIdMappings,
        };
    }, shallowEqual);

    const disableClear = useAppSelector((state) => {
        const question = selectQuestion(state, questionId);
        switch (question?.type) {
            case SurveyQuestionTypeEnum.NpsQuestion:
                return npsScore === undefined && !npsTextAnswer;

            case SurveyQuestionTypeEnum.FreeTextQuestion:
            case AssessmentFormQuestionTypeEnum.FreeTextQuestion:
                return !freeTextAnswer;

            case EvaluationQuestionTypeEnum.MultipleSelectQuestion:
                return (
                    !currentAnswerIdsForMSQ ||
                    currentAnswerIdsForMSQ.length === 0
                );

            default:
                return !answerId && !markedNa && !freeTextAnswer;
        }
    });

    const isCommentRequired = useAppSelector((state) => {
        return selectQuestion(state, questionId)?.commentsRequired ?? false;
    });
    const [showQuestionComments, setShowQuestionComments] = useState(false);
    const showCommentTextArea =
        (comments !== undefined && comments !== "") || showQuestionComments;
    const allowComments = useAppSelector(
        (state) =>
            selectFormType(state) !== FormType.SURVEY &&
            selectQuestion(state, questionId)?.type !==
                AssessmentFormQuestionTypeEnum.FreeTextQuestion
    );

    const formType = useAppSelector(selectFormType);
    const { previewMode } = useContext(PreviewModeContext);

    const commentCharLimitDateCheck = useAppSelector(
        selectCommentCharLimitDateCheck
    );
    const enforceCommentLimit =
        formType === FormType.EVALUATION &&
        (commentCharLimitDateCheck || previewMode);
    const hasError =
        enforceCommentLimit &&
        comments &&
        comments.length > DefaultLimits.generalCommentCharLimit;

    return (
        <div className="QuestionOperations">
            <>
                <div>
                    <button
                        className={`clear-answer-button ${
                            disableClear ? "" : "Clickable"
                        }`}
                        disabled={disableClear}
                        aria-disabled={disableClear}
                        onClick={() => {
                            const clearAnswer = () => {
                                dispatch(
                                    scorableSliceActions.clearAnswer({
                                        questionId,
                                        naEnabled,
                                        isMultipleSelect,
                                        multipleSelectOptionsIdMappings:
                                            multipleSelectQuestionOptionIdMappings,
                                    })
                                );
                            };
                            dispatch(
                                sendAnswersDataOnFormDataChange(clearAnswer)
                            );
                            if (previewMode) {
                                dispatch(
                                    getPreviewScores({
                                        isScorableFinished: false,
                                    })
                                );
                            }
                        }}
                    >
                        <GuxIcon
                            icon-name="close"
                            className="answer-operation"
                            decorative={true}
                        />
                        <span className="gux-body-sm-regular">
                            {t.labels.clearAnswer}
                        </span>
                    </button>
                </div>
                {allowComments && (
                    <div className="add-or-clear-comment">
                        {showCommentTextArea ? (
                            <button
                                className="Clickable"
                                onClick={() => {
                                    const clearComment = () => {
                                        dispatch(
                                            scorableSliceActions.fillQuestionComments(
                                                {
                                                    questionId,
                                                    newComments: "",
                                                }
                                            )
                                        );
                                    };
                                    dispatch(
                                        sendAnswersDataOnFormDataChange(
                                            clearComment
                                        )
                                    );
                                    setShowQuestionComments(false);
                                }}
                            >
                                <GuxIcon
                                    icon-name="close"
                                    className="answer-operation"
                                    decorative={true}
                                />
                                <span className="gux-body-sm-regular">
                                    {t.labels.clearComments}
                                </span>
                            </button>
                        ) : (
                            <button
                                className="Clickable"
                                onClick={() => {
                                    setShowQuestionComments(true);
                                }}
                            >
                                <GuxIcon
                                    icon-name="comments"
                                    className="answer-operation"
                                    decorative={true}
                                />
                                <span
                                    className={`gux-body-sm-regular ${
                                        isCommentRequired ? "required" : ""
                                    }`}
                                >
                                    {t.labels.addComments}
                                </span>
                            </button>
                        )}
                    </div>
                )}
            </>

            {showCommentTextArea && (
                <div className="comment" data-invalid={hasError === true}>
                    <div className="text-box">
                        <GuxFormFieldTextarea
                            resize="auto"
                            label-position="screenreader"
                        >
                            <textarea
                                slot="input"
                                name="textarea"
                                data-testid="answer-comments-text-box"
                                placeholder={t.labels.addCommentsInTextBox}
                                onChange={({ currentTarget }) => {
                                    const setComment = () => {
                                        dispatch(
                                            scorableSliceActions.fillQuestionComments(
                                                {
                                                    questionId,
                                                    newComments:
                                                        currentTarget.value ??
                                                        "",
                                                }
                                            )
                                        );
                                    };
                                    dispatch(
                                        sendAnswersDataOnFormDataChange(
                                            setComment
                                        )
                                    );
                                }}
                                defaultValue={comments}
                            ></textarea>
                            <label slot="label">{t.labels.addComments}</label>
                            {hasError && (
                                <span slot="error">
                                    {t.labels.commentCharLimitExceeded({
                                        characterLimit:
                                            DefaultLimits.generalCommentCharLimit,
                                    })}
                                </span>
                            )}
                        </GuxFormFieldTextarea>
                        {enforceCommentLimit && (
                            <RemainingCharacters
                                currentCount={comments?.length}
                                totalCount={
                                    DefaultLimits.generalCommentCharLimit
                                }
                            />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
